import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Initialize } from "./state/actions/Search";
import VariantRequest from "./VariantRequest";
import FeedbackForm from "./FeedbackForm";
import Toaster from "./components/Toaster";
import UserQuestions from "./pages/variant/components/UserQuestions";
import helpers from "./common/utils/utils";
import { FULLNAMECOOKIE } from "./common/helpers/Constant";
import TooltipRenderer from "./components/atoms/Tooltip/TooltipRenderer";
import EmailVerification from "./components/EmailVerification";
import ModalDesigned from "./components/atoms/Modal/ModalDesigned";
import localStorageActions from "./common/helpers/localStorageActions";
import VarsomePage from "./pages/VarsomePage";
import Loader from "./components/Loader";
import {
  getInfo,
  getQueryType,
  getVariantResult,
  getEmailVerificationState,
  getUserEmail,
  getUserName,
  getVarsomeHost,
  getSsoUrl,
  getError,
  getLoading,
  getVerificationSent,
  getIsModalOpen,
  getUserId,
} from "./state/selectors/general.selectors";
import {
  setFullNamePublic,
  setMobile,
  setPrint,
  setLoading,
} from "./state/actions/genericActions";
import { setUser } from "./state/actions/userActions";
import apiClient from "./services/apiClient";
import IntermediateScreen from "./pages/variant/VariantInfo/IntermediateScreen";
import Wizard from "./common/wizard/wizard";

import "../scss/varsome-style.scss";
import "../scss/minimum-style.scss";
import "../tailwind/tailwind.scss";
import "../scss/app.scss";

function App({
  setPrintAction,
  setMobileAction,
  setUserAction,
  setLoadingAction,
  setFullNamePublicAction,
  queryType,
  emailVerificationNeeded,
  userEmail,
  userName,
  varsomeHost,
  ssoUrl,
  error,
  loading,
  verificationSent,
  isModalOpen,
  userId,
  info,
  isIntermediate,
  hasNext,
}) {
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const isAccountSettings = queryType === "account-settings";
  const isEmailVerification = !isAccountSettings && emailVerificationNeeded;
  const showIntermediate =
    isIntermediate === "aminoacid" ||
    isIntermediate === "position" ||
    (isIntermediate === "rsid" && hasNext);

  useEffect(() => {
    if (userId) {
      setLoading(true);
      apiClient.getRemoteAuth({ url: ssoUrl }).subscribe(
        (user) => {
          const { full_name_public: fullNamePublic } = user;
          const cookieName = `${userId}${FULLNAMECOOKIE}`;
          setUserAction(user);
          setFullNamePublicAction(fullNamePublic);
          if (fullNamePublic === true) {
            helpers.createCookie(cookieName, true, 30);
          }
        },
        (err) => {
          setLoadingAction(false);
          setFullNamePublicAction(false);
          console.error("Error while getting user : ", err.res);
        }
      );
    }
  }, [userId]);

  useEffect(() => {
    // clear collapse state in case user has come from old ui
    localStorageActions.clearAllCollapsed();
  }, []);

  useEffect(() => {
    const mqPrint = matchMedia("print");
    const mqMobile = matchMedia("(max-width: 767px)");
    Initialize();
    window.addEventListener("popstate", Initialize);
    mqPrint.addListener(setPrintAction);
    mqMobile.addListener(setMobileAction);
    setMobile(mqMobile);
    setPrint(mqPrint);
  }, []);

  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [loading]);

  return isEmailVerification ? (
    <EmailVerification
      userEmail={userEmail}
      loading={loading}
      verificationSent={verificationSent}
      isModalOpen={isModalOpen}
      userName={userName}
      varsomeHost={varsomeHost}
      ssoUrl={ssoUrl}
    />
  ) : (
    <>
      {loading ? <Loader /> : null}

      {emailVerificationNeeded ? null : <VariantRequest />}

      {info ? (
        // eslint-disable-next-line react/no-danger
        <div className="varsome-request-info" dangerouslySetInnerHTML={info} />
      ) : null}

      {info || loading || error || showIntermediate || !queryType ? null : (
        <VarsomePage />
      )}

      {info || loading || error || !showIntermediate || !queryType ? null : (
        <IntermediateScreen />
      )}

      <Toaster />
      <UserQuestions />
      <FeedbackForm />
      <TooltipRenderer />
      {/* this.state.queryType !== 'community-contributions' && <RecentActivity /> */}
      <ModalDesigned
        isModalOpen={isReleaseModalOpen}
        setIsModalOpen={setIsReleaseModalOpen}
      />
      {info ||
      loading ||
      error ||
      showIntermediate ||
      !queryType ||
      isReleaseModalOpen ? null : (
        <Wizard />
      )}
    </>
  );
}

App.propTypes = {
  variantResult: PropTypes.shape({}),
  info: PropTypes.shape({}),
  queryType: PropTypes.string,
  emailVerificationNeeded: PropTypes.bool,
  userEmail: PropTypes.string,
  userName: PropTypes.string,
  varsomeHost: PropTypes.string,
  ssoUrl: PropTypes.string,
  error: PropTypes.shape({}),
  loading: PropTypes.bool,
  verificationSent: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  userId: PropTypes.string,
  setPrintAction: PropTypes.func.isRequired,
  setMobileAction: PropTypes.func.isRequired,
  setUserAction: PropTypes.func.isRequired,
  setLoadingAction: PropTypes.func.isRequired,
  setFullNamePublicAction: PropTypes.func.isRequired,
  isIntermediate: PropTypes.string,
  hasNext: PropTypes.bool,
};

App.defaultProps = {
  variantResult: {},
  info: null,
  queryType: "",
  emailVerificationNeeded: false,
  userEmail: null,
  userName: null,
  varsomeHost: null,
  ssoUrl: null,
  error: null,
  loading: true,
  verificationSent: false,
  isModalOpen: false,
  userId: null,
  isIntermediate: "",
  hasNext: false,
};

const mapStateToProps = (state) => ({
  variantResult: getVariantResult(state),
  info: getInfo(state),
  queryType: getQueryType(state),
  emailVerificationNeeded: getEmailVerificationState(state),
  userEmail: getUserEmail(state),
  userName: getUserName(state),
  varsomeHost: getVarsomeHost(state),
  ssoUrl: getSsoUrl(state),
  error: getError(state),
  loading: getLoading(state),
  verificationSent: getVerificationSent(state),
  isModalOpen: getIsModalOpen(state),
  userId: getUserId(state),
  isIntermediate: state.isIntermediate,
  hasNext: state.hasNext,
});

const mapDispatchToProps = (dispatch) => ({
  setPrintAction: (params) => dispatch(setPrint(params)),
  setMobileAction: (params) => dispatch(setMobile(params)),
  setUserAction: (params) => dispatch(setUser(params)),
  setLoadingAction: (params) => dispatch(setLoading(params)),
  setFullNamePublicAction: (params) => dispatch(setFullNamePublic(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
